import { useState, useRef, useCallback } from 'react'
import * as THREE from 'three'
import type { MapboxCustomLayerService } from '@/services/3d'
import { useAnimationEngine } from './useAnimationEngine'
import {
  SCENARIOS,
  resolveScenarioId,
  type Strategy,
  type DepartureWindow,
  type ScenarioId,
} from '../data/scenarios'
import { heroAircraftEntry } from '../data/fleetConfig'

export type DemoState =
  | 'loading'
  | 'baseline'
  | 'incoming_alert'
  | 'strategy_select'
  | 'animating'
  | 'result'

export function useDemoOrchestrator(
  layerServiceRef: React.RefObject<MapboxCustomLayerService | null>
) {
  const [demoState, setDemoState] = useState<DemoState>('loading')
  const [activeScenarioId, setActiveScenarioId] = useState<ScenarioId>('baseline')
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null)
  const [departureWindow, setDepartureWindow] = useState<DepartureWindow>('morning')
  const heroAddedRef = useRef(false)

  const { animateToScenario } = useAnimationEngine(layerServiceRef)

  /** Called by PlannerDemoPage once MapboxScene is ready and fleet is loaded */
  const onSceneReady = useCallback(() => {
    setDemoState('baseline')
  }, [])

  /** Step 1: Show incoming flight notification */
  const triggerIncoming = useCallback(async () => {
    if (demoState !== 'baseline') return

    // Add the hero aircraft to the scene (outside doors)
    if (!heroAddedRef.current) {
      const layerService = layerServiceRef.current
      if (layerService) {
        await layerService.addAircraft(heroAircraftEntry)
        heroAddedRef.current = true

        // Apply gold emissive highlight to hero
        const sceneObjects = layerService.getSceneObjects()
        const heroObj = sceneObjects.aircraft.find(
          (a) => a.name === heroAircraftEntry.id
        )
        if (heroObj) {
          heroObj.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              const mesh = child as THREE.Mesh
              const materials = Array.isArray(mesh.material)
                ? mesh.material
                : [mesh.material]
              materials.forEach((mat) => {
                if ((mat as THREE.MeshStandardMaterial).emissive !== undefined) {
                  ;(mat as THREE.MeshStandardMaterial).emissive = new THREE.Color(
                    0xcc7700
                  )
                  ;(mat as THREE.MeshStandardMaterial).emissiveIntensity = 0.4
                }
              })
            }
          })
          layerService.markSceneDirty()
        }
      }
    }

    setDemoState('incoming_alert')
  }, [demoState, layerServiceRef])

  /** Step 2: Move to strategy selection */
  const showStrategySelect = useCallback(() => {
    setDemoState('strategy_select')
  }, [])

  /** Step 3: User picks a strategy — animate aircraft to scenario positions */
  const selectStrategy = useCallback(
    async (strategy: Strategy) => {
      if (demoState !== 'strategy_select' && demoState !== 'result') return

      setSelectedStrategy(strategy)
      setDemoState('animating')

      const scenarioId = resolveScenarioId(strategy, departureWindow)
      const scenario = SCENARIOS[scenarioId]

      const targets = Object.entries(scenario.poses).map(([id, pose]) => ({
        aircraftId: id,
        toPosition: pose.position as [number, number, number],
        toRotation: pose.rotation,
      }))

      await animateToScenario(targets, 1400)

      setActiveScenarioId(scenarioId)
      setDemoState('result')
    },
    [demoState, departureWindow, animateToScenario]
  )

  /** Departure time slider — re-animate to matching scenario for current strategy */
  const adjustDeparture = useCallback(
    async (window: DepartureWindow) => {
      if (!selectedStrategy) return
      if (demoState !== 'result') return

      setDepartureWindow(window)
      setDemoState('animating')

      const scenarioId = resolveScenarioId(selectedStrategy, window)
      const scenario = SCENARIOS[scenarioId]

      const targets = Object.entries(scenario.poses).map(([id, pose]) => ({
        aircraftId: id,
        toPosition: pose.position as [number, number, number],
        toRotation: pose.rotation,
      }))

      await animateToScenario(targets, 1000)

      setActiveScenarioId(scenarioId)
      setDemoState('result')
    },
    [selectedStrategy, demoState, animateToScenario]
  )

  /** Reset back to baseline (for re-demo) */
  const resetDemo = useCallback(async () => {
    const baseline = SCENARIOS['baseline']
    const targets = Object.entries(baseline.poses).map(([id, pose]) => ({
      aircraftId: id,
      toPosition: pose.position as [number, number, number],
      toRotation: pose.rotation,
    }))

    setDemoState('animating')

    // Remove hero from scene
    const layerService = layerServiceRef.current
    if (layerService && heroAddedRef.current) {
      layerService.removeAircraft(heroAircraftEntry.id)
      heroAddedRef.current = false
    }

    await animateToScenario(targets, 1000)

    setActiveScenarioId('baseline')
    setSelectedStrategy(null)
    setDepartureWindow('morning')
    setDemoState('baseline')
  }, [layerServiceRef, animateToScenario])

  return {
    demoState,
    activeScenarioId,
    selectedStrategy,
    departureWindow,
    onSceneReady,
    triggerIncoming,
    showStrategySelect,
    selectStrategy,
    adjustDeparture,
    resetDemo,
  }
}
